import React, { Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { CelebrationPersona, EnrichedCelebration } from './celebration.types';
import {
  CELEBRATION_BODY,
  CELEBRATION_CLOSE,
  CELEBRATION_EYEBROW,
  CELEBRATION_OVERLAY,
  CELEBRATION_PANEL,
  CELEBRATION_PRIMARY_CTA,
  CELEBRATION_SECONDARY_CTA,
  CELEBRATION_TITLE,
} from './celebration.tokens';
import { CelebrationNextStep } from './CelebrationNextStep';

type Props = {
  open: boolean;
  item: EnrichedCelebration | null;
  persona: CelebrationPersona;
  icon: React.ReactNode;
  testId?: string;
  onClose: () => void;
};

/** Tier 2 — bottom sheet, light scrim, dashboard stays visible. */
export const CelebrationSheet: React.FC<Props> = ({
  open,
  item,
  persona,
  icon,
  testId = 'celebration-tier2-sheet',
  onClose,
}) => {
  const navigate = useNavigate();

  if (!item) return null;

  const goPrimary = () => {
    onClose();
    navigate(item.primaryCtaHref);
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog className="relative z-[200]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 ${CELEBRATION_OVERLAY} pointer-events-none`}
            aria-hidden
          />
        </TransitionChild>

        <div className="pointer-events-none fixed inset-0 z-[201] flex items-end justify-center sm:items-end">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-250"
            enterFrom="opacity-0 translate-y-full"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-full"
          >
            <DialogPanel
              className={`pointer-events-auto w-full max-w-lg rounded-t-2xl sm:mx-4 sm:mb-4 sm:rounded-2xl ${CELEBRATION_PANEL[persona]}`}
              data-testid={testId}
            >
              <div className="flex justify-center pt-2 sm:hidden" aria-hidden>
                <span className="h-1 w-10 rounded-full bg-slate-200" />
              </div>

              <div className="relative px-5 pb-5 pt-3 sm:px-6 sm:pb-6 sm:pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className={`absolute right-3 top-3 sm:right-4 sm:top-4 ${CELEBRATION_CLOSE}`}
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                <div className="flex items-start gap-4 pr-8">
                  <div className="shrink-0">{icon}</div>
                  <div className="min-w-0 flex-1">
                    <p className={CELEBRATION_EYEBROW[persona]}>{item.eyebrow}</p>
                    <h2 className={`${CELEBRATION_TITLE} text-left`}>{item.title}</h2>
                    <p className={`mt-2 text-left ${CELEBRATION_BODY}`}>{item.whyItMatters}</p>
                    {item.xp && item.xp > 0 ? (
                      <p className="mt-2 text-xs font-medium text-slate-500">
                        +{item.xp} activity points
                      </p>
                    ) : null}
                  </div>
                </div>

                <CelebrationNextStep item={item} persona={persona} className="mt-4" />

                <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={goPrimary}
                    className={`${CELEBRATION_PRIMARY_CTA[persona]} sm:flex-1`}
                  >
                    {item.primaryCtaLabel}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className={`${CELEBRATION_SECONDARY_CTA[persona]} sm:flex-1`}
                  >
                    {item.secondaryCtaLabel}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
